<template>
  <div class="checkout-step-two">
    <div class="checkout-step__header">
      <div class="row align-items-end">
        <div class="col-12 col-md-9">
          <h3 class="checkout-step__headline">
            {{ $t('pages.checkoutStepTwo.title') }}
          </h3>
          <p class="checkout-step__copy">
            {{ $t('pages.checkoutStepTwo.copy') }}
          </p>
        </div>
        <div class="col-12 col-md-3">
          <small class="checkout-step__copy checkout-step__copy--small">
            {{ $t('generic.priceInfo') }}
          </small>
        </div>
      </div>
    </div>

    <div class="campsite-type-selector">
      <div class="row">
        <div class="col-sm-6" v-for="(campsiteType, index) in campsiteTypes" :key="index">
          <div class="campsite-type white-box"
               :class="{ active: steps[2].selectedCampsiteType === campsiteType[0] }"
               @click="setCampsiteType(campsiteType)">
            {{ campsiteType[1] }}
          </div>
        </div>
      </div>
    </div>

    <div class="campsite-selector" ref="campsiteSelector">
      <div class="campsite"
           v-for="campsite in getAvailableCampsites"
           v-if="campsite.campsite_type === steps[2].selectedCampsiteType"
           :key="campsite.id">

        <div class="campsite__inner white-box"
             :class="[ { active: isSelectedCampsite(campsite) }, campsite.amount > 0 ? 'clickable' : 'campsite__inner--disabled' ]"
             @click="campsite.amount > 0 && setSelectedCampsite(campsite)">
          <div class="row align-items-center">
            <div class="col-6 col-md-3 order-2 order-md-1">
              <div class="campsite__status">
                <div class="status__circle" v-if="campsite.amount > 0" :class="{ 'status__circle--active': isSelectedCampsite(campsite) }" >
                  <iconCheckmarkWhite class="icon"></iconCheckmarkWhite>
                </div>
                <span class="warning" v-if="campsite.amount <= 0" :class="{ 'status__circle--active': isSelectedCampsite(campsite) }" >
                  {{ $t(`pages.checkoutStepTwo.campsiteNotAvailableLabel`) }}
                  <iconLock class="icon"></iconLock>
                </span>
              </div>
            </div>
            <div class="col-12 col-md-6 order-1 order-md-2">
              <div class="campsite__meta">
                <h3 class="meta__headline">{{ campsite.name }}</h3>
                <div class="meta__description" v-html="campsite.description"></div>
              </div>
            </div>
            <div class="col-6 col-md-3 order-1 order-md-3">
              <div class="campsite__price" v-if="campsite.amount > 0">
                <span class="price__value">{{ campsite.prices.total | currency(campground.currency, language.code) }}</span>
                <span class="price__value price__value--alt" v-if="campsite.prices.total_alt">{{ campsite.prices.total_alt | currency(currency.code, language.code) }}</span>
                <span class="price__meta">{{ $tc('pages.checkoutStepTwo.priceInfo', numNights > 1 ? 0 : 1, { nights: numNights }) }}</span>
              </div>
            </div>
          </div>
          <div class="campsite__reason" v-if="campsite.amount <= 0">
            <div class="has-icon">
              <iconWarning class="icon"></iconWarning>
            </div>
            <div class="reason-title">{{ $t(`pages.checkoutStepTwo.campsiteNotAvailableTitle`) }}</div>
            <div class="reason-content" v-if="campsite.reason">
              {{ $t(`pages.checkoutStepTwo.campsiteNotAvailableReasons.${campsite.reason}`) }}
              <span v-if="campsite.reason === 'invalidArrivalAndDepartureWeekday' || campsite.reason === 'invalidArrivalWeekday' || campsite.reason === 'invalidDepartureWeekday'">
                <span class="restricted-weekdays">
                  <span v-if="campsite.reason === 'invalidArrivalAndDepartureWeekday' || campsite.reason === 'invalidArrivalWeekday'" v-html="$t('pages.checkoutStepTwo.restrictedArrivalWeekdays', {days: getWeekdaysString(campsite.arrival_weekdays)}) + ' '"></span>
                  <span v-if="campsite.reason === 'invalidArrivalAndDepartureWeekday' || campsite.reason === 'invalidDepartureWeekday'" v-html="$t('pages.checkoutStepTwo.restrictedDeparturelWeekdays', {days: getWeekdaysString(campsite.departure_weekdays)}) + ' '"></span>
                  <a class="action" href="#" @click="goToStep({ number: 1, key: 'displaySelection', value: 'dates' })">{{ $t('pages.checkoutStepTwo.changeTravelDates') }}</a>
                </span>
              </span>
            </div>
          </div>
        </div>

        <CiProgressNavigation
          class="campground__navigation--inline"
          v-if="isSelectedCampsite(campsite)"
          :active-step="activeStep"
          :status="status"
          :steps="steps">
        </CiProgressNavigation>
      </div>
    </div>

    <CiProgressNavigation
      class="campground__navigation--inline"
      v-if="selectedCampsite === null"
      :active-step="activeStep"
      :status="status"
      :steps="steps">
    </CiProgressNavigation>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { CiProgressNavigation, CiInfoBox, CiSelectionBox } from '@/components'
import { isElementInViewport } from '@/utils/general'
import { getJsDate } from '@/utils/date'
import { mapGetters, mapActions } from 'vuex'

import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconArrowWhite from '@/assets/icons/arrow-white.svg'
import iconCheckmarkWhite from '@/assets/icons/checkmark-white.svg'
import iconLock from '@/assets/icons/lock-solid.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'

export default {
  name: 'CheckoutStepTwo',
  components: {
    CiInfoBox,
    CiProgressNavigation,
    CiSelectionBox,
    iconArrowBlack,
    iconArrowWhite,
    iconCheckmarkWhite,
    iconLock,
    iconWarning,
  },
  created() {
    this.$snowplow.trackStep('pitch_type_selection')
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      steps: 'steps',
    }),

    ...mapGetters('booking', {
      dates: 'dates',
      selectedCampsite: 'selectedCampsite',
      selectedPersons: 'selectedPersons',
      selectedSeasons: 'selectedSeasons',
    }),

    ...mapGetters('campground', {
      availableCampsites: 'availableCampsites',
      campground: 'campground',
      campsiteTypes: 'campsiteTypes',
      status: 'status',
    }),

    getAvailableCampsites() {
      // const filtered = this.availableCampsites.filter(campsite => campsite.amount > 0)
      return this.availableCampsites.map(campsite => {
        const basicCampsite = this.campground.campsites.find(cp => cp.id === campsite.id)
        return { ...basicCampsite, ...campsite }
      })
    },

    numNights() {
      const d = this.dates
      if (!d || !d[1] || d[0] == null || d[1] == null) {
        return 0
      }

      const d1 = DateTime.fromJSDate(getJsDate(d[0]))
      const d2 = DateTime.fromJSDate(getJsDate(d[1]))

      return Math.round(Math.abs(d2.diff(d1, 'days').as('days')))
    },
  },
  methods: {
    ...mapActions('application', {
      setStepStatus: 'setStepStatus',
    }),

    ...mapActions('booking', {
      setSelectedCampsite: 'setSelectedCampsite',
    }),

    setCampsiteType(campsiteType) {
      // show warning modal if campsite is selected and users switches campsite type
      if (this.selectedCampsite) {
        this.$modal.show('dialog', {
          title: this.$t('modals.CiModalCampsiteTypeWarning.title'),
          content: this.$t('modals.CiModalCampsiteTypeWarning.content'),
          proceedEvent: /* istanbul ignore next */ () => this.onChangeCampsiteType(campsiteType),
          selectedCampsite: this.selectedCampsite,
        })
      } else {
        // set selected campsite type into step status
        this.setStepStatus({ number: 2, key: 'selectedCampsiteType', value: campsiteType[0] })

        // scroll campsites into viewport if they aren't
        this.$nextTick(() => {
          /* istanbul ignore else */
          if (!isElementInViewport(this.$refs.campsiteSelector)) {
            this.$refs.campsiteSelector.scrollIntoView({ block: 'start', behavior: 'smooth' })
          }
        })
      }
    },

    // if user agrees to change campsite type inside the warning modal
    onChangeCampsiteType(campsiteType) {
      this.setSelectedCampsite(null)
      this.setStepStatus({ number: 2, key: 'selectedCampsiteType', value: campsiteType[0] })
    },

    isSelectedCampsite(campsite) {
      if (!campsite) return
      return (
        campsite.campsite_type === this.steps[2].selectedCampsiteType &&
        (this.selectedCampsite && campsite.id === this.selectedCampsite.id)
      )
    },

    getWeekdaysString(days) {
      let names = []
      let idx = 0
      let hasSunday = false
      for (let day = 4; day <= 10; day++) {
        if (days.indexOf(idx) !== -1) {
          if (idx === 0) {
            hasSunday = true
          } else {
            names.push(
              new Date(1970, 0, day).toLocaleString(this.language.code, { weekday: 'long' })
            )
          }
        }
        idx++
      }
      if (hasSunday) {
        names.push(new Date(1970, 0, 4).toLocaleString(this.language.code, { weekday: 'long' }))
      }
      return names.join(', ')
    },

    goToStep(stepData) {
      this.setStepStatus(stepData)
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: stepData.number },
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.checkout-step-two {
  padding: 0;

  @include media-breakpoint-up(md) {
    padding: 0 0 60px;
  }

  @include media-breakpoint-up(lg) {
    padding: 0;
  }
}

.checkout-step-two__header {
  margin: 0 0 30px;
}

.checkout-step-two__headline {
  @include font-format(1.375rem, 1.625em);
}

.campsite-type-selector {
  margin-bottom: 10px;

  @include media-breakpoint-up(xl) {
    margin-bottom: 30px;
  }
}

.campsite {
  margin: 0 0 20px;
}

.campsite__inner {
  margin: 0;
  padding: 0;
  position: relative;
  transition: border 0.4s ease;

  @include media-breakpoint-up(md) {
    padding: 25px;
  }

  &.campsite__inner--disabled {
    color: #9b9b9b;
    background-image: repeating-linear-gradient(
      45deg,
      #f6f6f6,
      #f6f6f6 5px,
      #fcfcfc 5px,
      #fcfcfc 20px
    );
    border: 1px solid rgba(155, 155, 155, 0.5);
    padding-bottom: 0;

    .no-touch &:hover {
      border: 1px solid rgba(155, 155, 155, 0.5);
    }
  }

  &.active,
  .no-touch &:hover {
    border-color: $primary-green;
  }
}

.campsite__reason {
  @include clearfix;
  @include font-format(0.75rem, 1.25rem);

  color: black;
  background-color: #fefcdd;
  padding: 12px 15px;
  margin: 0;

  @include media-breakpoint-up(md) {
    margin: 25px -25px 0;
  }

  .icon {
    width: 20px;
    height: auto;
  }

  & > div {
    float: left;
  }

  .has-icon {
    width: 40px;
  }
  .reason-title {
    @include font('gotham_mediumregular');

    width: 150px;
  }
  .reason-content {
    width: 100%;
    margin-top: 10px;
    @include media-breakpoint-up(sm) {
      width: calc(100% - 190px);
      margin-top: 0;
    }

    .action {
      color: $primary-orange;
      text-decoration: underline;
      font-weight: bold;

      &:hover {
        text-decoration: none;
      }
    }
  }
}

.campsite-type {
  @include font('gotham_mediumregular');

  align-items: center;
  display: flex;
  height: 60px;
  justify-content: center;
  margin: 0 0 10px;
  cursor: pointer;
  transition: background-color 0.4s ease;

  .no-touch &:hover,
  &.active {
    color: white;
    background: $primary-green;
  }
}

.campsite__status {
  align-items: center;
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: flex-end;
  padding: 20px;

  @include media-breakpoint-up(md) {
    justify-content: center;
    padding: 0;
  }

  .status__circle {
    align-items: center;
    background: #cdcccd;
    border-radius: 50%;
    display: flex;
    height: 30px;
    justify-content: center;
    opacity: 0.5;
    width: 30px;
    transition: background-color 0.6s ease;
    align-self: flex-end;

    .icon {
      width: 17px;
      height: auto;
    }

    &.status__circle--active {
      opacity: 1;
      background: $primary-green;
    }

    @include media-breakpoint-up(md) {
      height: 45px;
      width: 45px;

      .icon {
        width: 27px;
        height: auto;
      }
    }
  }

  .warning {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1rem);

    position: relative;
    background-color: #fefcda;
    border-radius: 8px;
    color: #f5a623;
    padding: 8px 32px 6px 12px;
    text-transform: uppercase;

    .icon {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-10px, -50%);
      width: 11px;
      height: auto;
      fill: #f5a623;
    }
  }
}

.campsite__meta {
  border-bottom: 1px solid hsla(0, 0%, 60.8%, 0.5);
  flex: 3;
  padding: 20px;

  @include media-breakpoint-up(md) {
    border: 0;
    padding: 0;
  }

  .meta__headline {
    @include font-format(1rem, 1.625em);

    margin-bottom: 15px;
  }

  .meta__description {
    @include font-format(1rem, 1.25rem);

    margin: 0;
    padding: 0;
  }
}

.campsite__price {
  @include font('gotham_mediumregular');
  @include font-format(1.375rem, 1.625em);

  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 0 10px 20px;

  @include media-breakpoint-up(md) {
    padding: 0 20px 0 0;
    align-items: center;
  }

  .price__value--alt {
    @include font-format(1rem, 1.25em);

    color: #9b9b9b;
    margin: 0 0 5px 0;
  }

  .price__meta {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1.625em);

    color: #9b9b9b;
    margin: 0 10px 0 0;
  }
}
</style>
